/* iter367 Phase-3 — Privacy Dashboard
 *
 * One-stop UI for all granular privacy preferences:
 *   • Art. 22 profiling opt-out toggle (GAP-8)
 *   • Granular consent toggles (reproductive_health_v1, biometric_voice_image_v1) (GAP-4)
 *   • Revoke health-data consent (GAP-7) → cascade-clears sensitive profile fields
 *   • Link to AI System Register (GAP-5)
 *   • Link to Data Retention Policy + DPIA
 *
 * Designed as a drop-in widget; can be embedded anywhere (Profile page,
 * standalone /privacy route, etc.).
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Shield, BrainCircuit, Heart, Mic, Trash2, ExternalLink, Loader2,
  AlertTriangle, CheckCircle2,
} from "lucide-react";

export default function PrivacyDashboard({ isDark = false }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const bg = language === "bg";

  const [profiling, setProfiling] = useState(null);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pp, cs] = await Promise.all([
        axios.get(`${API}/me/gdpr/profiling-preferences`),
        axios.get(`${API}/me/gdpr/consents`),
      ]);
      setProfiling(pp.data);
      setConsents(cs.data?.consents || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (bg ? "Грешка при зареждане" : "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, [API, bg]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleProfiling = async () => {
    setBusy("profiling");
    try {
      const { data } = await axios.post(`${API}/me/gdpr/profiling-preferences`, {
        profiling_enabled: !profiling?.profiling_enabled,
      });
      setProfiling((prev) => ({ ...prev, ...data }));
      toast.success(data.profiling_enabled
        ? (bg ? "Персонализиран анализ е включен." : "Personalised analysis enabled.")
        : (bg ? "Персонализиран анализ е изключен. Анализите използват само макрос данни." : "Personalised analysis disabled. Analyses use macro data only."));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const toggleConsent = async (kind, currentlyAccepted) => {
    setBusy(kind);
    try {
      await axios.post(`${API}/me/gdpr/consents/${kind}`, {
        accepted: !currentlyAccepted, version: "v1",
      });
      toast.success(bg ? "Запазено." : "Saved.");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const revokeHealthConsent = async () => {
    setBusy("revoke");
    try {
      const { data } = await axios.post(`${API}/me/gdpr/health-consent/revoke`, {});
      toast.success(bg ? data.next_steps_bg : data.next_steps_en, { duration: 8000 });
      setConfirmingRevoke(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="privacy-dashboard">
      <header className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-emerald-500" />
        <h2 className={`text-base font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
          {bg ? "Поверителност и контрол върху данните" : "Privacy & data control"}
        </h2>
      </header>

      {/* GAP-8: Profiling opt-out */}
      <Section title={bg ? "Персонализиран анализ" : "Personalised analysis"} isDark={isDark}
                icon={<BrainCircuit className="w-4 h-4" />}>
        <p className={`text-[12px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {profiling?.[bg ? "info_bg" : "info_en"]}
        </p>
        <div className="flex items-center justify-between mt-2">
          <span className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
            {profiling?.profiling_enabled
              ? (bg ? "✅ Включен (използва Вашия профил)" : "✅ Enabled (uses your profile)")
              : (bg ? "⏸️ Изключен (само макрос данни)" : "⏸️ Disabled (macro-only)")}
          </span>
          <Button
            onClick={toggleProfiling}
            disabled={busy === "profiling"}
            variant="outline"
            data-testid="profiling-toggle-btn"
          >
            {busy === "profiling" && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {profiling?.profiling_enabled
              ? (bg ? "Изключи" : "Disable")
              : (bg ? "Включи" : "Enable")}
          </Button>
        </div>
      </Section>

      {/* GAP-4: Granular consents */}
      <Section title={bg ? "Чувствителни категории данни" : "Sensitive data categories"} isDark={isDark}
                icon={<Heart className="w-4 h-4" />}>
        <ul className="space-y-2">
          {consents.filter((c) => c.kind !== "health_data_processing").map((c) => (
            <li key={c.kind} className={`rounded-lg p-2.5 ${isDark ? "bg-white/5" : "bg-gray-50"}`} data-testid={`consent-row-${c.kind}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {c.kind === "biometric_voice_image_v1" && <Mic className="w-3 h-3" />}
                    <span className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                      {bg ? c.label_bg : c.label_en}
                    </span>
                  </div>
                  <p className={`text-[11px] leading-relaxed mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {bg ? c.description_bg : c.description_en}
                  </p>
                  <p className={`text-[10px] mt-1 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {c.legal_basis}
                  </p>
                </div>
                <Button
                  onClick={() => toggleConsent(c.kind, c.accepted)}
                  disabled={busy === c.kind}
                  size="sm"
                  variant="outline"
                  data-testid={`consent-toggle-${c.kind}`}
                >
                  {busy === c.kind && <Loader2 className="w-3 h-3 animate-spin mr-1" />}
                  {c.accepted ? (bg ? "Изключи" : "Disable") : (bg ? "Включи" : "Enable")}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {/* GAP-7: Revoke health consent */}
      <Section title={bg ? "Оттегляне на здравното съгласие" : "Revoke health-data consent"} isDark={isDark}
                icon={<AlertTriangle className="w-4 h-4 text-amber-500" />}>
        <p className={`text-[12px] leading-relaxed ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          {bg
            ? "Оттеглянето на здравното съгласие незабавно изтрива всички чувствителни полета от Вашия профил (състояния, лекарства, цикъл, добавки). Можете да възстановите съгласие или да изтриете акаунта си напълно."
            : "Revoking health-data consent immediately clears all sensitive profile fields (conditions, medications, cycle, supplements). You may restore consent later or delete your account entirely."}
        </p>
        {!confirmingRevoke ? (
          <Button
            variant="outline"
            onClick={() => setConfirmingRevoke(true)}
            className="mt-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="revoke-health-consent-btn"
          >
            <AlertTriangle className="w-4 h-4 mr-1" />
            {bg ? "Оттегли здравното съгласие" : "Revoke health-data consent"}
          </Button>
        ) : (
          <div className={`mt-2 p-3 rounded-lg border ${isDark ? "bg-red-500/10 border-red-500/30" : "bg-red-50 border-red-200"}`}>
            <p className={`text-[12px] font-semibold ${isDark ? "text-red-200" : "text-red-900"}`}>
              {bg ? "Сигурни ли сте? Това действие е необратимо." : "Are you sure? This action is irreversible."}
            </p>
            <div className="flex gap-2 mt-2">
              <Button
                onClick={revokeHealthConsent}
                disabled={busy === "revoke"}
                className="bg-red-500 hover:bg-red-600 text-white"
                data-testid="revoke-health-consent-confirm"
              >
                {busy === "revoke" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                {bg ? "Да, оттегли" : "Yes, revoke"}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setConfirmingRevoke(false)}
                data-testid="revoke-health-consent-cancel"
              >
                {bg ? "Откажи" : "Cancel"}
              </Button>
            </div>
          </div>
        )}
      </Section>

      {/* GAP-5: AI Register + Resources */}
      <Section title={bg ? "Документи и прозрачност" : "Documents & transparency"} isDark={isDark}
                icon={<CheckCircle2 className="w-4 h-4" />}>
        <ul className="space-y-1.5 text-[12px]">
          <ResourceLink
            href={`${API}/me/gdpr/ai-systems/register`}
            label={bg ? "AI System Register (EU AI Act чл. 49)" : "AI System Register (EU AI Act Art. 49)"}
            isDark={isDark}
            testId="link-ai-register"
          />
          <ResourceLink
            href={`${API}/me/gdpr/ai-decisions`}
            label={bg ? "Моите AI решения (GDPR чл. 22)" : "My AI decisions (GDPR Art. 22)"}
            isDark={isDark}
            testId="link-ai-decisions"
          />
          <ResourceLink
            href={`${API}/me/gdpr/export`}
            label={bg ? "Експорт на личните ми данни (GDPR чл. 15)" : "Export my personal data (GDPR Art. 15)"}
            isDark={isDark}
            testId="link-gdpr-export"
          />
          <li className={`text-[11px] italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
            {bg
              ? "Жалба към КЗЛД (Комисия за защита на личните данни): "
              : "Complaint to Bulgarian DPA (CPDP): "}
            <a href="https://www.cpdp.bg" target="_blank" rel="noopener noreferrer"
                className="underline text-emerald-500" data-testid="link-cpdp">www.cpdp.bg</a>
          </li>
        </ul>
      </Section>
    </div>
  );
}

function Section({ title, icon, isDark, children }) {
  return (
    <section className={`rounded-xl border p-3 ${isDark ? "bg-white/5 border-white/10" : "bg-white border-gray-200"}`}>
      <div className={`flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
        {icon}{title}
      </div>
      {children}
    </section>
  );
}

function ResourceLink({ href, label, isDark, testId }) {
  return (
    <li>
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
          className={`inline-flex items-center gap-1.5 underline hover:no-underline ${isDark ? "text-emerald-300" : "text-emerald-600"}`}>
        <ExternalLink className="w-3 h-3" />{label}
      </a>
    </li>
  );
}
